import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Clock } from "lucide-react";

/**
 * Waiting and continuation-chain display for long builds (ENG-335).
 *
 * A build that hits the AI usage limit is parked as "waiting" until its
 * `resumeAt`; a build cut short is picked up by a continuation job
 * (`continuedFromJobId`), up to `maxContinuations` of them.
 */

/** The chain fields a job (or an active-job summary) carries. */
export interface JobChainFields {
  continuedFromJobId?: string | null;
  continuationIndex?: number;
  nextContinuationJobId?: string | null;
  maxContinuations?: number;
}

/** Fallback when the server did not say (it does from ENG-335 on). */
const DEFAULT_MAX_CONTINUATIONS = 5;

/** True when the job is part of a continuation chain (has a parent or a child). */
export function isInContinuationChain(job: JobChainFields): boolean {
  return Boolean(job.continuedFromJobId) || (job.continuationIndex ?? 0) > 0 || Boolean(job.nextContinuationJobId);
}

/**
 * The resume time in the browser's locale and time zone, e.g. "Oct 23, 4:30 PM",
 * or null when there is no (valid) time.
 */
export function formatResumeTime(resumeAt: string | null | undefined, locale?: string): string | null {
  if (!resumeAt) return null;
  const date = new Date(resumeAt);
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat(locale, { dateStyle: "medium", timeStyle: "short" }).format(date);
}

/** "Waiting for the AI usage window. Resumes at …" (or "Resumes soon"). */
export function waitingMessage(resumeAt: string | null | undefined, locale?: string): string {
  const when = formatResumeTime(resumeAt, locale);
  return `Waiting for the AI usage window. ${when ? `Resumes at ${when}` : "Resumes soon"}.`;
}

export function WaitingNotice({ resumeAt }: { resumeAt: string | null | undefined }) {
  return (
    <span className="inline-flex items-center gap-1.5" data-testid="waiting-notice">
      <Clock className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400" />
      {waitingMessage(resumeAt)}
    </span>
  );
}

/**
 * "Continuation N of up to 5 · ← Previous run · Next run →". Renders nothing
 * for a job outside any chain.
 */
export function ContinuationLinks({ job }: { job: JobChainFields }) {
  if (!isInContinuationChain(job)) return null;
  const index = job.continuationIndex ?? 0;
  const max = job.maxContinuations ?? DEFAULT_MAX_CONTINUATIONS;
  return (
    <div className="flex flex-wrap items-center gap-3 text-sm" data-testid="continuation-links">
      <span className="text-muted-foreground">
        {index === 0 ? "Original run" : `Continuation ${index} of up to ${max}`}
      </span>
      {job.continuedFromJobId && (
        <Link
          to={`/mailbox/jobs/${job.continuedFromJobId}`}
          className="inline-flex items-center gap-1 text-primary hover:underline"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          Previous run
        </Link>
      )}
      {job.nextContinuationJobId && (
        <Link
          to={`/mailbox/jobs/${job.nextContinuationJobId}`}
          className="inline-flex items-center gap-1 text-primary hover:underline"
        >
          Next run
          <ArrowRight className="h-3.5 w-3.5" />
        </Link>
      )}
    </div>
  );
}
